import {
  ResponseTypeEnum,
  SectionTypeEnum,
  TemplateNodeTypeEnum,
  type TemplateNode,
} from '@ulysses-inc/harami_api_client'
import { UUID } from 'src/exShared/util/uuid'
import {
  MigrationDomainError,
  type ConvertedMigration,
  type MigrationSource,
  type UuidMappings,
} from './types'
import { createTemporaryIdAllocator, createUuidMappings } from './uuidMapping'

const mappedUuid = (uuid: string, mappings: UuidMappings): string => {
  const mapped = mappings.nodeUuids.get(uuid)
  if (!mapped) {
    throw new MigrationDomainError([
      `参照先Node UUID「${uuid}」を変換できません。`,
    ])
  }
  return mapped
}

const rewriteQuestionReferences = (
  node: TemplateNode,
  mappings: UuidMappings,
): TemplateNode['question'] => {
  if (!node.question) return undefined
  const question = structuredClone(node.question)

  question.responseFormulas = question.responseFormulas?.map(formula => ({
    ...formula,
    tokens: formula.tokens?.map(token => ({
      ...token,
      ...(token.questionNodeUUID
        ? { questionNodeUUID: mappedUuid(token.questionNodeUUID, mappings) }
        : {}),
    })),
  }))
  question.responseTimeMeasurements = question.responseTimeMeasurements?.map(
    measurement => ({
      ...measurement,
      startQuestionNodeUUID: mappedUuid(
        measurement.startQuestionNodeUUID,
        mappings,
      ),
      endQuestionNodeUUID: mappedUuid(
        measurement.endQuestionNodeUUID,
        mappings,
      ),
    }),
  )
  question.hints = []
  return question
}

/**
 * 旧「表形式」のひな形を表セクションのPage/Nodeツリーへ変換し、識別子を付け替える。
 *
 * 変換結果では取得用の共有型を流用するため、Nodeには数値IDが必要になる。
 * Page/Nodeにはコピー元と重複しない負数を変換中だけ使う一時IDとして割り当て、
 * コピー元と変換先を区別する。負値自体はAPI要件ではなく、保存時はネスト構造から
 * 新しい数値IDが採番されるため、一時IDは保存後IDを指定する値ではない。
 *
 * NOTE: この移行では変更範囲を抑えるため、IDを持たない作成専用の再帰型は導入せず、取得用の共有型を流用している。
 * 将来的には作成専用型を導入し、一時IDへの依存を解消する。
 *
 * Node UUIDは保存・参照される識別子なので、すべて新規採番し、計算式と時間計測内の
 * 参照も同じ対応表で書き換える。限定選択肢IDはここでは維持し、後続のworkflowで
 * 新しいIDへ書き換える。
 */
export const convertGridToTable = (
  source: MigrationSource,
  createUuid: () => string = UUID,
): ConvertedMigration => {
  const sourcePages = source.pagesWithRule.pages
  const mappings = createUuidMappings(sourcePages, createUuid)
  const nextId = createTemporaryIdAllocator(sourcePages)

  const cloneNode = (node: TemplateNode): TemplateNode => {
    const targetUuid = mappings.nodeUuids.get(node.uuid)
    if (!targetUuid) {
      throw new MigrationDomainError([
        `Node UUID「${node.uuid}」の対応関係がありません。`,
      ])
    }
    const cloned: TemplateNode = {
      id: nextId(),
      uuid: targetUuid,
      type: node.type,
      ...(node.section ? { section: structuredClone(node.section) } : {}),
      ...(node.question
        ? { question: rewriteQuestionReferences(node, mappings) }
        : {}),
      ...(node.logic ? { logic: structuredClone(node.logic) } : {}),
      ...(node.hide ? { hide: structuredClone(node.hide) } : {}),
      ...(node.traceKey ? { traceKey: structuredClone(node.traceKey) } : {}),
      nodes: (node.nodes ?? [])
        .filter(
          child =>
            child.question?.responseType !== ResponseTypeEnum.GRID_VARIABLE,
        )
        .map(cloneNode),
    }
    // section/question/logicは新規レコードとして保存するため、コピー元のDB識別子を作成リクエストへ持ち込まない
    delete cloned.section?.id
    delete cloned.question?.id
    delete cloned.logic?.id
    return cloned
  }

  const pages = sourcePages.map(page => {
    const root = page.nodes?.[0]
    if (!root || root.type !== TemplateNodeTypeEnum.Section) {
      throw new MigrationDomainError([
        `ページ「${page.name}」のルートセクションを変換できません。`,
      ])
    }
    const convertedRoot = cloneNode(root)
    const row: TemplateNode = {
      id: nextId(),
      uuid: createUuid(),
      type: TemplateNodeTypeEnum.Section,
      section: {
        name: '',
        isRepeat: 1,
        isEmployeeCheck: 0,
        sectionType: SectionTypeEnum.TableRow,
      },
      nodes: convertedRoot.nodes ?? [],
    }
    convertedRoot.section = {
      ...convertedRoot.section,
      isRepeat: 0,
      isEmployeeCheck: 0,
      sectionType: SectionTypeEnum.Table,
    }
    convertedRoot.nodes = [row]
    return { id: nextId(), name: page.name, nodes: [convertedRoot] }
  })

  return { pages, mappings }
}
